import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Loader2, Info, AlertCircle, Send } from "lucide-react";

const STEPS = ["Servizio", "Disponibilità", "Conferma"];

export default function NuovaRichiesta() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const [f, setF] = useState({
    service_id: "", municipality: "", address: "", recipient_name: "", contact_phone: "",
    requested_by: "paziente", description: "", logistics_note: "",
    preferred_date: "", preferred_time_range: "mattina", urgency: "normale", accept_alternative_slot: true,
  });

  const { data: svcData } = useQuery({ queryKey: ["services-all"], queryFn: async () => (await api.get("/services?only_active=false")).data });
  const { data: munData } = useQuery({ queryKey: ["municipalities"], queryFn: async () => (await api.get("/config/municipalities")).data });
  const services = (svcData?.services || []).filter((s) => s.is_category);
  const municipalities = munData?.municipalities || [];
  const selectedService = services.find((s) => s.id === f.service_id);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  const step1Valid = f.service_id && f.municipality && f.address.trim().length > 2 && f.recipient_name.trim() && f.contact_phone.trim().length >= 5 && f.description.trim().length >= 5;
  const step2Valid = f.preferred_date && f.preferred_time_range;

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      await api.post("/requests", {
        service_id: f.service_id, municipality: f.municipality, address: f.address,
        recipient_name: f.recipient_name, contact_phone: f.contact_phone, requested_by: f.requested_by,
        description: f.description, logistics_note: f.logistics_note, preferred_date: f.preferred_date,
        preferred_time_range: f.preferred_time_range, urgency: f.urgency,
        accept_alternative_slot: f.accept_alternative_slot, consent_booking_data: consent,
      });
      toast.success("Richiesta inviata! Un medico ti contatterà a breve.");
      navigate("/paziente/richieste");
    } catch (e) { setError(apiError(e)); setSubmitting(false); }
  };

  return (
    <DashboardLayout title="Nuova richiesta">
      {/* stepper */}
      <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <div className="flex flex-col items-center">
              <span className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm ${
                i < step ? "bg-[#087A73] text-white" : i === step ? "bg-[#087A73] text-white ring-4 ring-[#087A73]/20" : "bg-[#EAF3EF] text-[#5C6B68]"}`}>
                {i < step ? <Check className="h-5 w-5" /> : i + 1}
              </span>
              <span className={`text-xs mt-1 font-semibold hidden sm:block ${i <= step ? "text-[#102A2A]" : "text-[#5C6B68]"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 w-8 sm:w-16 mx-1 ${i < step ? "bg-[#087A73]" : "bg-[#D2E3DB]"}`} />}
          </div>
        ))}
      </div>

      {error && <div data-testid="wizard-error" className="mb-4 flex items-start gap-2 bg-[#F9E3E0] border border-[#C0392B]/30 text-[#C0392B] rounded-xl px-4 py-3 text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {error}</div>}

      <div className="mb-4 flex items-start gap-2 bg-[#FBF0D6] border border-[#D99A00]/40 text-[#8a6400] rounded-xl px-4 py-3 text-sm">
        <Info className="h-4 w-4 mt-0.5 shrink-0" /> Non inserire informazioni cliniche o dati sanitari nei campi descrittivi. Raccogliamo solo dati organizzativi.
      </div>

      {/* STEP 1 */}
      {step === 0 && (
        <Card className="space-y-5">
          <h3 className="text-lg font-bold text-[#102A2A]">Di quale servizio hai bisogno?</h3>
          <div>
            <Label>Tipo di servizio *</Label>
            <Select value={f.service_id} onValueChange={(v) => setF({ ...f, service_id: v })}>
              <SelectTrigger data-testid="wizard-service-select" className="h-12 rounded-xl mt-1 bg-white"><SelectValue placeholder="Seleziona un servizio" /></SelectTrigger>
              <SelectContent className="bg-white">
                {services.map((s) => <SelectItem key={s.id} value={s.id} disabled={!s.active} data-testid={`svc-opt-${s.slug}`}>{s.name}{!s.active ? " · Prossimamente" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Comune *</Label>
              <Select value={f.municipality} onValueChange={(v) => setF({ ...f, municipality: v })}>
                <SelectTrigger data-testid="wizard-municipality-select" className="h-12 rounded-xl mt-1 bg-white"><SelectValue placeholder="Seleziona comune" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {municipalities.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Indirizzo della visita *</Label><Input data-testid="wizard-address" value={f.address} onChange={upd("address")} className="h-12 rounded-xl mt-1" placeholder="Via, numero civico" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Nome di chi riceve la visita *</Label><Input data-testid="wizard-recipient" value={f.recipient_name} onChange={upd("recipient_name")} className="h-12 rounded-xl mt-1" placeholder="Es. Maria Bianchi" /></div>
            <div><Label>Numero di telefono *</Label><Input data-testid="wizard-phone" value={f.contact_phone} onChange={upd("contact_phone")} className="h-12 rounded-xl mt-1" placeholder="333 1234567" /></div>
          </div>
          <div>
            <Label>Chi effettua la richiesta?</Label>
            <RadioGroup value={f.requested_by} onValueChange={(v) => setF({ ...f, requested_by: v })} className="flex gap-6 mt-2">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="paziente" data-testid="req-by-patient" /> Paziente</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="familiare" data-testid="req-by-family" /> Familiare</label>
            </RadioGroup>
          </div>
          <div><Label>Descrivi di cosa hai bisogno *</Label><Textarea data-testid="wizard-description" value={f.description} onChange={upd("description")} className="rounded-xl mt-1" placeholder="Es. Ho bisogno di una visita generale / di una ricetta per un farmaco / di una medicazione..." /></div>
          <div><Label>Indicazioni logistiche (non cliniche)</Label><Textarea data-testid="wizard-logistics" value={f.logistics_note} onChange={upd("logistics_note")} className="rounded-xl mt-1" placeholder="Es. citofono, piano, parcheggio disponibile" /></div>
          <div className="flex justify-end">
            <Button data-testid="wizard-step1-next" disabled={!step1Valid} onClick={() => setStep(1)} className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-12 px-6 font-semibold">
              Continua <ChevronRight className="ml-1 h-5 w-5" />
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 2 */}
      {step === 1 && (
        <Card className="space-y-5">
          <h3 className="text-lg font-bold text-[#102A2A]">Quando preferisci la visita?</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Giorno preferito *</Label><Input type="date" data-testid="wizard-date" value={f.preferred_date} min={new Date().toISOString().split("T")[0]} onChange={upd("preferred_date")} className="h-12 rounded-xl mt-1" /></div>
            <div>
              <Label>Fascia oraria preferita *</Label>
              <Select value={f.preferred_time_range} onValueChange={(v) => setF({ ...f, preferred_time_range: v })}>
                <SelectTrigger data-testid="wizard-timerange" className="h-12 rounded-xl mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="mattina">Mattina (09:00 - 13:00)</SelectItem>
                  <SelectItem value="pomeriggio">Pomeriggio (14:00 - 18:00)</SelectItem>
                  <SelectItem value="sera">Sera (18:00 - 20:00)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Urgenza organizzativa</Label>
            <RadioGroup value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })} className="flex gap-6 mt-2">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="normale" data-testid="urgency-normal" /> Normale</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="prioritaria" data-testid="urgency-priority" /> Prioritaria</label>
            </RadioGroup>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox data-testid="wizard-alt-slot" checked={f.accept_alternative_slot} onCheckedChange={(v) => setF({ ...f, accept_alternative_slot: !!v })} />
            <span className="text-sm text-[#5C6B68]">Sono disponibile a concordare un orario alternativo con il medico</span>
          </label>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)} className="rounded-xl h-12 px-6 border-2 border-[#D2E3DB]"><ChevronLeft className="mr-1 h-5 w-5" /> Indietro</Button>
            <Button data-testid="wizard-step2-next" disabled={!step2Valid} onClick={() => setStep(2)} className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-12 px-6 font-semibold">
              Continua <ChevronRight className="ml-1 h-5 w-5" />
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3 */}
      {step === 2 && (
        <Card className="space-y-5">
          <h3 className="text-lg font-bold text-[#102A2A]">Conferma e invia la richiesta</h3>
          <div className="flex items-start gap-2 bg-[#EAF3EF] border border-[#D2E3DB] text-[#5C6B68] rounded-xl px-4 py-3 text-sm">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#087A73]" /> Invii una richiesta ai medici disponibili per questo servizio. Un medico la prenderà in carico e ti contatterà al recapito indicato per concordare la visita e la tariffa.
          </div>
          <div className="bg-[#EAF3EF] rounded-2xl p-5 space-y-3 text-sm">
            <Row label="Servizio" value={selectedService?.name} />
            <Row label="Descrizione" value={f.description} />
            <Row label="Comune" value={f.municipality} />
            <Row label="Indirizzo" value={f.address} />
            <Row label="Riceve la visita" value={f.recipient_name} />
            <Row label="Recapito" value={f.contact_phone} />
            <Row label="Preferenza" value={`${f.preferred_date} · ${f.preferred_time_range}`} />
            {f.urgency === "prioritaria" && <Row label="Urgenza" value="Prioritaria" />}
          </div>
          <label className="flex items-start gap-3 cursor-pointer bg-white border border-[#D2E3DB] rounded-xl p-4">
            <Checkbox data-testid="request-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
            <span className="text-sm text-[#5C6B68]">Acconsento al trattamento dei dati necessari alla richiesta, secondo l'<a href="/legal/privacy" target="_blank" className="text-[#087A73] font-semibold underline">informativa privacy</a>. *</span>
          </label>
          <div className="flex justify-between">
            <Button variant="outline" disabled={submitting} onClick={() => setStep(1)} className="rounded-xl h-12 px-6 border-2 border-[#D2E3DB]"><ChevronLeft className="mr-1 h-5 w-5" /> Indietro</Button>
            <Button data-testid="wizard-submit" disabled={!consent || submitting} onClick={submit} className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-12 px-6 font-semibold">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Invia richiesta <Send className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        </Card>
      )}
    </DashboardLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-[#5C6B68]">{label}</span>
      <span className="font-semibold text-[#102A2A] text-right">{value}</span>
    </div>
  );
}
